import type { VolumeSnapshot, SnapshotSchedule } from '@/service/gpu-instance/volume-type';
import { SnapshotStatus } from '@/service/gpu-instance/volume-type';

const now = Date.now();
const hour = 3600000;
const day = 24 * hour;

export const mockSnapshots: VolumeSnapshot[] = [
  // Snapshots for vol-001 (ml-training-data)
  {
    id: 'snap-001',
    name: 'daily-backup-2024-01-22',
    volumeId: 'vol-001',
    volumeName: 'ml-training-data',
    status: SnapshotStatus.Ready,
    size: 342,
    createdAt: now - 6 * hour,
    description: 'Automated daily backup',
    isScheduled: true,
    scheduleId: 'sched-001',
    scheduleName: 'Daily Backup',
    restorable: true,
    labels: { type: 'scheduled', retention: '7d' },
  },
  {
    id: 'snap-002',
    name: 'daily-backup-2024-01-21',
    volumeId: 'vol-001',
    volumeName: 'ml-training-data',
    status: SnapshotStatus.Ready,
    size: 338,
    createdAt: now - 1 * day - 6 * hour,
    description: 'Automated daily backup',
    isScheduled: true,
    scheduleId: 'sched-001',
    scheduleName: 'Daily Backup',
    restorable: true,
    labels: { type: 'scheduled', retention: '7d' },
  },
  {
    id: 'snap-003',
    name: 'pre-training-checkpoint',
    volumeId: 'vol-001',
    volumeName: 'ml-training-data',
    status: SnapshotStatus.Ready,
    size: 320,
    createdAt: now - 5 * day,
    description: 'Manual snapshot before starting new training run',
    isScheduled: false,
    restorable: true,
    labels: { type: 'manual', purpose: 'checkpoint' },
  },
  {
    id: 'snap-004',
    name: 'epoch-100-milestone',
    volumeId: 'vol-001',
    volumeName: 'ml-training-data',
    status: SnapshotStatus.Ready,
    size: 335,
    createdAt: now - 3 * day,
    description: 'Training milestone at epoch 100',
    isScheduled: false,
    restorable: true,
    labels: { type: 'manual', epoch: '100' },
  },
  {
    id: 'snap-005',
    name: 'weekly-backup-2024-w03',
    volumeId: 'vol-001',
    volumeName: 'ml-training-data',
    status: SnapshotStatus.Ready,
    size: 340,
    createdAt: now - 2 * day,
    description: 'Weekly backup snapshot',
    isScheduled: true,
    scheduleId: 'sched-002',
    scheduleName: 'Weekly Backup',
    restorable: true,
    labels: { type: 'scheduled', retention: '30d' },
  },

  // Snapshots for vol-002 (model-checkpoints)
  {
    id: 'snap-006',
    name: 'hourly-2024-01-22-08',
    volumeId: 'vol-002',
    volumeName: 'model-checkpoints',
    status: SnapshotStatus.Ready,
    size: 756,
    createdAt: now - 1 * hour,
    description: 'Hourly checkpoint backup',
    isScheduled: true,
    scheduleId: 'sched-003',
    scheduleName: 'Hourly Checkpoints',
    restorable: true,
    labels: { type: 'scheduled', retention: '24h' },
  },
  {
    id: 'snap-007',
    name: 'hourly-2024-01-22-07',
    volumeId: 'vol-002',
    volumeName: 'model-checkpoints',
    status: SnapshotStatus.Ready,
    size: 752,
    createdAt: now - 2 * hour,
    description: 'Hourly checkpoint backup',
    isScheduled: true,
    scheduleId: 'sched-003',
    scheduleName: 'Hourly Checkpoints',
    restorable: true,
    labels: { type: 'scheduled', retention: '24h' },
  },
  {
    id: 'snap-008',
    name: 'best-model-v2.1',
    volumeId: 'vol-002',
    volumeName: 'model-checkpoints',
    status: SnapshotStatus.Ready,
    size: 745,
    createdAt: now - 5 * day,
    description: 'Best performing model checkpoint - keep indefinitely',
    isScheduled: false,
    restorable: true,
    labels: { type: 'manual', important: 'true', model_version: '2.1' },
  },
  {
    id: 'snap-009',
    name: 'pre-finetune-baseline',
    volumeId: 'vol-002',
    volumeName: 'model-checkpoints',
    status: SnapshotStatus.Ready,
    size: 720,
    createdAt: now - 10 * day,
    description: 'Baseline before fine-tuning experiment',
    isScheduled: false,
    restorable: true,
    labels: { type: 'manual', experiment: 'finetune-001' },
  },

  // Snapshots for vol-003 (inference-models)
  {
    id: 'snap-010',
    name: 'production-release-v3.0',
    volumeId: 'vol-003',
    volumeName: 'inference-models',
    status: SnapshotStatus.Ready,
    size: 145,
    createdAt: now - 12 * hour,
    description: 'Production model release v3.0',
    isScheduled: false,
    restorable: true,
    labels: { type: 'release', version: '3.0', environment: 'production' },
  },
  {
    id: 'snap-011',
    name: 'production-release-v2.9',
    volumeId: 'vol-003',
    volumeName: 'inference-models',
    status: SnapshotStatus.Ready,
    size: 142,
    createdAt: now - 7 * day,
    description: 'Production model release v2.9 - previous stable',
    isScheduled: false,
    restorable: true,
    labels: { type: 'release', version: '2.9', environment: 'production' },
  },
  {
    id: 'snap-012',
    name: 'daily-inference-backup',
    volumeId: 'vol-003',
    volumeName: 'inference-models',
    status: SnapshotStatus.Ready,
    size: 145,
    createdAt: now - 1 * day,
    description: 'Daily backup of inference models',
    isScheduled: true,
    scheduleId: 'sched-004',
    scheduleName: 'Daily Inference Backup',
    restorable: true,
    labels: { type: 'scheduled', retention: '14d' },
  },

  // Snapshots for vol-005 (dataset-archive)
  {
    id: 'snap-013',
    name: 'monthly-archive-2024-01',
    volumeId: 'vol-005',
    volumeName: 'dataset-archive',
    status: SnapshotStatus.Ready,
    size: 3200,
    createdAt: now - 1 * day,
    description: 'Monthly archive snapshot',
    isScheduled: true,
    scheduleId: 'sched-005',
    scheduleName: 'Monthly Archive',
    restorable: true,
    labels: { type: 'scheduled', retention: '365d' },
  },
  {
    id: 'snap-014',
    name: 'dataset-v2-complete',
    volumeId: 'vol-005',
    volumeName: 'dataset-archive',
    status: SnapshotStatus.Ready,
    size: 2800,
    createdAt: now - 30 * day,
    description: 'Complete dataset v2 archive',
    isScheduled: false,
    restorable: true,
    labels: { type: 'manual', dataset_version: '2.0' },
  },

  // Snapshot being created
  {
    id: 'snap-015',
    name: 'creating-snapshot',
    volumeId: 'vol-010',
    volumeName: 'shared-datasets',
    status: SnapshotStatus.Creating,
    size: 0,
    createdAt: now - 2 * 60000,
    description: 'Creating new snapshot...',
    isScheduled: false,
    restorable: false,
    labels: {},
  },

  // Snapshot being restored
  {
    id: 'snap-016',
    name: 'restore-test-snap',
    volumeId: 'vol-008',
    volumeName: 'problematic-volume',
    status: SnapshotStatus.Restoring,
    size: 245,
    createdAt: now - 3 * day,
    description: 'Attempting to restore from this snapshot',
    isScheduled: false,
    restorable: false,
    labels: { type: 'recovery' },
  },

  // Error snapshot
  {
    id: 'snap-017',
    name: 'failed-snapshot',
    volumeId: 'vol-008',
    volumeName: 'problematic-volume',
    status: SnapshotStatus.Error,
    size: 0,
    createdAt: now - 2 * day,
    description: 'Snapshot creation failed due to storage error',
    isScheduled: true,
    scheduleId: 'sched-006',
    restorable: false,
    labels: { type: 'scheduled', error: 'true' },
  },
];

export const mockSchedules: SnapshotSchedule[] = [
  {
    id: 'sched-001',
    name: 'Daily Backup',
    volumeId: 'vol-001',
    volumeName: 'ml-training-data',
    frequency: 'daily',
    retentionCount: 7,
    retentionDays: 7,
    enabled: true,
    lastRun: now - 6 * hour,
    nextRun: now + 18 * hour,
    createdAt: now - 30 * day,
    snapshotPrefix: 'daily-backup',
  },
  {
    id: 'sched-002',
    name: 'Weekly Backup',
    volumeId: 'vol-001',
    volumeName: 'ml-training-data',
    frequency: 'weekly',
    retentionCount: 4,
    retentionDays: 30,
    enabled: true,
    lastRun: now - 2 * day,
    nextRun: now + 5 * day,
    createdAt: now - 30 * day,
    snapshotPrefix: 'weekly-backup',
  },
  {
    id: 'sched-003',
    name: 'Hourly Checkpoints',
    volumeId: 'vol-002',
    volumeName: 'model-checkpoints',
    frequency: 'hourly',
    retentionCount: 24,
    retentionDays: 1,
    enabled: true,
    lastRun: now - 1 * hour,
    nextRun: now,
    createdAt: now - 45 * day,
    snapshotPrefix: 'hourly',
  },
  {
    id: 'sched-004',
    name: 'Daily Inference Backup',
    volumeId: 'vol-003',
    volumeName: 'inference-models',
    frequency: 'daily',
    retentionCount: 14,
    retentionDays: 14,
    enabled: true,
    lastRun: now - 1 * day,
    nextRun: now + 23 * hour,
    createdAt: now - 60 * day,
    snapshotPrefix: 'daily-inference-backup',
  },
  {
    id: 'sched-005',
    name: 'Monthly Archive',
    volumeId: 'vol-005',
    volumeName: 'dataset-archive',
    frequency: 'custom',
    cronExpression: '0 0 1 * *', // First day of each month at midnight
    retentionCount: 12,
    retentionDays: 365,
    enabled: true,
    lastRun: now - 1 * day,
    nextRun: now + 9 * day, // Approximate
    createdAt: now - 90 * day,
    snapshotPrefix: 'monthly-archive',
  },
  {
    id: 'sched-006',
    name: 'Weekly Standard Backup',
    volumeId: 'vol-008',
    volumeName: 'problematic-volume',
    frequency: 'weekly',
    retentionCount: 4,
    enabled: false, // Disabled due to volume errors
    lastRun: now - 7 * day,
    nextRun: now, // Would have run but disabled
    createdAt: now - 10 * day,
    snapshotPrefix: 'weekly-standard',
  },
  {
    id: 'sched-007',
    name: 'Bi-Weekly Datasets',
    volumeId: 'vol-010',
    volumeName: 'shared-datasets',
    frequency: 'custom',
    cronExpression: '0 0 * * 0,3', // Sundays and Wednesdays at midnight
    retentionCount: 8,
    retentionDays: 28,
    enabled: true,
    lastRun: now - 4 * day,
    nextRun: now + 3 * day,
    createdAt: now - 75 * day,
    snapshotPrefix: 'bi-weekly-datasets',
  },
];

export const getSnapshotsForVolume = (volumeId: string): VolumeSnapshot[] => {
  return mockSnapshots
    .filter((snap) => snap.volumeId === volumeId)
    .sort((a, b) => b.createdAt - a.createdAt);
};

export const getSchedulesForVolume = (volumeId: string): SnapshotSchedule[] => {
  return mockSchedules
    .filter((sched) => sched.volumeId === volumeId)
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const getSnapshotById = (id: string): VolumeSnapshot | undefined => {
  return mockSnapshots.find((snap) => snap.id === id);
};

export const getScheduleById = (id: string): SnapshotSchedule | undefined => {
  return mockSchedules.find((sched) => sched.id === id);
};
